import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';
import { useSiteLang } from '@/components/site/SiteLang';
import { CalendarCheck, ChevronDown, ArrowRight, CheckCircle2, Users, Sparkles } from 'lucide-react';

// Agenda de Toni (02/11/2026). Si en Admin › Ajustes del test hay otro enlace, manda ese.
const DEFAULT_BOOKING_URL = 'https://calendly.com/toni-freedomhomes/30min';

// Sin lenguaje de inversión (09/10/2026): acceso a oportunidades, formación, comunidad,
// certificación privada y eventos; sin cifras, rentabilidades ni plazos de respuesta en horas.
// Textos ES / EN (inglés británico, 04/11/2026). El español no se toca.
const T = {
  es: {
    headlineA: 'Tu test está',
    headlineB: 'recibido.',
    heroText: 'El siguiente paso es una llamada corta con el equipo para conocerte, resolver tus dudas y explicarte qué nivel encaja contigo. Si aún no has elegido día y hora, hazlo ahora.',
    booking: 'Elegir día y hora',
    meanwhile: 'Mientras tanto, esta página existe para que llegues a esa conversación con claridad total y las preguntas que realmente importan.',
    nextLabel: 'Lo que pasa ahora',
    nextReview: 'Tu perfil está siendo revisado por el equipo de Freedom Homes Club.',
    nextBooking: 'Eliges día y hora en la agenda para una conversación de 15 a 30 minutos: un espacio real para resolver todo lo que necesitas aclarar, conocer la cuota del nivel que encaja contigo y ver si tiene sentido avanzar juntos.',
    nextRestricted: 'Freedom Homes Club es un club de acceso restringido. No todo el mundo es aceptado — y eso es precisamente lo que hace que los que están dentro encuentren exactamente lo que vinieron a buscar.',
    faqLabel: 'Preguntas que probablemente tienes',
    faqTitle: 'Las respuestas que más nos piden antes de la conversación.',
    faq: [
      {
        q: '¿Qué incluye cada membresía?',
        a: [
          'Access: pertenencia al club, comunidad privada, área privada de oportunidades, contenidos introductorios y sesiones online.',
          'PRO: todo lo de Access, más formación avanzada, sesiones especializadas en directo y recursos ampliados.',
          'Premium: todo lo de PRO, más el Bootcamp Broker Digital presencial, la certificación privada FHC y acompañamiento grupal semanal.',
        ],
      },
      {
        q: '¿Qué tipo de oportunidades hay dentro del club?',
        a: [
          'Oportunidades inmobiliarias reales que el equipo y sus colaboradores trabajan fuera de los portales. Se presentan en el área privada con su documentación y su análisis.',
          'Cada miembro decide libremente si participa y en cuál, después de formarse. La membresía te da acceso al club; participar es siempre una decisión aparte y voluntaria.',
        ],
      },
      {
        q: '¿Qué estoy pagando exactamente?',
        a: [
          'La cuota corresponde exclusivamente al acceso al club, la comunidad privada, los contenidos formativos, las sesiones y los eventos incluidos en el nivel contratado.',
        ],
      },
      {
        q: '¿Qué pasa después de la llamada?',
        a: [
          'Si hay encaje, creas tu cuenta en la plataforma, eliges tu membresía y recibes una sesión de bienvenida para orientarte desde el primer día.',
        ],
      },
      {
        q: '¿La certificación es oficial?',
        a: [
          'La certificación FHC es privada e interna del club, sin equivalencia académica oficial. Se obtiene al completar el programa Premium.',
        ],
      },
    ],
    clarLabel: 'Dos cosas que vale la pena aclarar',
    clarifications: [
      {
        title: '"Solo quiero formarme. No me interesa certificarme."',
        text: 'Es perfectamente válido. Access y PRO te dan pertenencia, comunidad, formación y sesiones, sin ninguna obligación de certificarte. El Bootcamp y la certificación privada forman parte de Premium y son una opción, no un requisito.',
      },
      {
        title: '"¿Tengo que vender algo o traer a otras personas?"',
        text: 'No. La membresía vale por lo que incluye: comunidad, formación, sesiones y eventos. Presentar el club como broker certificado es una posibilidad para quien completa Premium, nunca una condición.',
      },
    ],
    convLabel: 'Qué esperar de la conversación',
    convIntro: 'La conversación dura entre 15 y 30 minutos y tiene tres partes.',
    convSteps: [
      { num: '01', text: 'Primero nos conocemos — quién eres, de dónde vienes, qué te trajo hasta aquí.' },
      { num: '02', text: 'Luego resolvemos lo que necesites sobre las membresías, la formación o el programa de certificación.' },
      { num: '03', text: 'Y al final valoramos juntos si hay encaje real entre lo que buscas y lo que Freedom Homes Club ofrece.' },
    ],
    convMutual: 'Es un proceso de selección mutua. Nosotros evaluamos si eres el perfil que buscamos. Tú evalúas si somos el lugar adecuado para lo que estás construyendo.',
    convQuestions: 'Llega con tus preguntas reales. Es lo que hace que la conversación valga la pena.',
    backHome: 'Volver al inicio',
  },
  en: {
    headlineA: 'Your test is',
    headlineB: 'in.',
    heroText: 'The next step is a short call with the team to get to know you, answer your questions and explain which tier suits you. If you haven’t chosen a day and time yet, do it now.',
    booking: 'Choose a day and time',
    meanwhile: 'In the meantime, this page is here so you come to that conversation with complete clarity — and with the questions that really matter.',
    nextLabel: 'What happens now',
    nextReview: 'The Freedom Homes Club team is reviewing your profile.',
    nextBooking: 'You choose a day and time for a 15 to 30-minute conversation: a proper chance to clear up anything you need to, find out the fee for the tier that suits you and see whether it makes sense to move forward together.',
    nextRestricted: 'Access to Freedom Homes Club is restricted. Not everyone is accepted — and that is exactly why those who are find precisely what they came for.',
    faqLabel: 'Questions you probably have',
    faqTitle: 'What people most often ask us before the call.',
    faq: [
      {
        q: 'What does each membership include?',
        a: [
          'Access: membership of the club, the private community, the opportunities in the members’ area, introductory content and online sessions.',
          'PRO: everything in Access, plus advanced training, specialist live sessions and extended resources.',
          'Premium: everything in PRO, plus the in-person Digital Broker Bootcamp, the private FHC certification and weekly group support.',
        ],
      },
      {
        q: 'What kind of opportunities are there in the club?',
        a: [
          'Real property opportunities that the team and its partners work on away from the property portals. Each one is presented in the members’ area with its documentation and analysis.',
          'Every member decides freely whether to take part, and in which, once they have completed their training. Membership gives you access to the club; taking part is always a separate, voluntary decision.',
        ],
      },
      {
        q: 'What exactly am I paying for?',
        a: [
          'The fee covers only access to the club, the private community, the training content, and the sessions and events included in your tier.',
        ],
      },
      {
        q: 'What happens after the call?',
        a: [
          'If it’s a good fit, you create your account on the platform, choose your membership and have a welcome session to help you find your feet from day one.',
        ],
      },
      {
        q: 'Is the certification official?',
        a: [
          'The FHC certification is private and internal to the club, with no official academic equivalence. It is awarded on completing the Premium programme.',
        ],
      },
    ],
    clarLabel: 'Two things worth clearing up',
    clarifications: [
      {
        title: '‘I just want to learn. I’m not interested in getting certified.’',
        text: 'That’s perfectly fine. Access and PRO give you membership, community, training and sessions, with no obligation to get certified. The Bootcamp and the private certification are part of Premium — an option, not a requirement.',
      },
      {
        title: '‘Do I have to sell anything or bring other people in?’',
        text: 'No. Membership stands on what it includes: community, training, sessions and events. Introducing the club as a certified broker is open to those who complete Premium — never a condition.',
      },
    ],
    convLabel: 'What to expect from the call',
    convIntro: 'The conversation takes 15 to 30 minutes and has three parts.',
    convSteps: [
      { num: '01', text: 'First, we get to know each other — who you are, where you’re coming from and what brought you here.' },
      { num: '02', text: 'Then we answer whatever you need to know about the memberships, the training or the certification programme.' },
      { num: '03', text: 'Finally, we decide together whether there’s a genuine fit between what you’re looking for and what Freedom Homes Club offers.' },
    ],
    convMutual: 'It’s a two-way selection. We consider whether you’re the profile we’re looking for. You consider whether we’re the right place for what you’re building.',
    convQuestions: 'Bring your real questions. That’s what makes the conversation worthwhile.',
    backHome: 'Back to home',
  },
};

function AccordionItem({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-start justify-between gap-4 py-5 text-left group" data-testid="faq-toggle">
        <span className="font-body text-base font-semibold text-[#0F172A] group-hover:text-[#c9a84c] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-400 ${open ? 'max-h-[600px] pb-5' : 'max-h-0'}`}>
        {children}
      </div>
    </div>
  );
}

export default function ThankYou() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [bookingUrl, setBookingUrl] = useState(DEFAULT_BOOKING_URL);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/funnel/config`).then((r) => {
      const u = r.data?.booking_url;
      if (u && /^https:\/\//.test(u)) setBookingUrl(u);
    }).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#fafbfc]" data-testid="thank-you-page">

      {/* ── HERO ── */}
      <section className="pt-36 pb-20 bg-[#0a1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }} />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="w-20 h-20 mx-auto bg-[#c9a84c]/10 border border-[#c9a84c]/30 rounded-full flex items-center justify-center mb-8">
            <CheckCircle2 className="w-10 h-10 text-[#c9a84c]" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white leading-tight mb-6" data-testid="ty-headline">
            {t.headlineA} <span className="italic text-[#c9a84c]">{t.headlineB}</span>
          </h1>
          <p className="font-body text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            {t.heroText}
          </p>
          <a href={bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="ty-booking"
            className="mt-8 inline-flex max-w-full items-center justify-center gap-2 h-14 px-8 bg-[#c9a84c] text-[#0a1628] font-semibold hover:bg-[#d4b85c] transition-colors">
            <CalendarCheck className="w-5 h-5" /> {t.booking}
          </a>
          <div className="mt-8 inline-flex items-center gap-2 border border-white/10 bg-white/5 px-5 py-3">
            <Sparkles className="w-4 h-4 text-[#c9a84c]" />
            <span className="font-body text-sm text-white/50">{t.meanwhile}</span>
          </div>
        </div>
      </section>

      {/* ── LO QUE PASA AHORA ── */}
      <section className="py-20 bg-[#fafbfc]" data-testid="ty-whats-next">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="font-body text-xs uppercase tracking-[0.25em] text-[#c9a84c] block mb-3">{t.nextLabel}</span>
          </div>
          <div className="bg-white border border-slate-200 p-5 sm:p-8 md:p-10">
            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-[#0F172A] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#c9a84c]" />
                </div>
                <p className="font-body text-sm text-slate-600 leading-relaxed pt-2">
                  {t.nextReview}
                </p>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#0F172A] flex items-center justify-center">
                  <CalendarCheck className="w-5 h-5 text-[#c9a84c]" />
                </div>
                <p className="font-body text-sm text-slate-600 leading-relaxed pt-2">
                  {t.nextBooking}
                </p>
              </div>
              <div className="border-t border-slate-100 pt-6">
                <p className="font-body text-sm text-[#0F172A] font-semibold leading-relaxed">
                  {t.nextRestricted}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PREGUNTAS FRECUENTES ── */}
      <section className="py-20 bg-white" data-testid="ty-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-xs uppercase tracking-[0.25em] text-[#c9a84c] block mb-3">{t.faqLabel}</span>
            <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A]">{t.faqTitle}</h2>
          </div>

          <div className="border border-slate-200 divide-y-0 px-4 sm:px-6">
            {t.faq.map((faq, i) => (
              <AccordionItem key={i} q={faq.q}>
                <div className="pl-0 space-y-3">
                  {faq.a.map((p, j) => (
                    <p key={j} className="font-body text-sm text-slate-600 leading-relaxed">{p}</p>
                  ))}
                  {faq.layers && (
                    <div className="space-y-4 mt-4">
                      {faq.layers.map((layer, k) => (
                        <div key={k} className="flex gap-3">
                          <div className="flex-shrink-0 w-1.5 h-full bg-[#c9a84c]/30 mt-1 self-stretch" />
                          <div>
                            <p className="font-body text-sm font-semibold text-[#0F172A]">{layer.title}</p>
                            <p className="font-body text-sm text-slate-500 leading-relaxed mt-1">{layer.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionItem>
            ))}
          </div>
        </div>
      </section>

      {/* ── DOS COSAS QUE VALE LA PENA ACLARAR ── */}
      <section className="py-20 bg-[#fafbfc]" data-testid="ty-clarifications">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-xs uppercase tracking-[0.25em] text-[#c9a84c] block mb-3">{t.clarLabel}</span>
          </div>

          <div className="space-y-6">
            {t.clarifications.map((item, i) => (
              <div key={i} className="bg-white border border-slate-200 p-6 md:p-8">
                <p className="font-heading text-lg text-[#0F172A] italic mb-4">{item.title}</p>
                <p className="font-body text-sm text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUÉ ESPERAR DE LA CONVERSACIÓN ── */}
      <section className="py-20 bg-[#0a1628]" data-testid="ty-conversation">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="font-body text-xs uppercase tracking-[0.25em] text-[#c9a84c] block mb-3">{t.convLabel}</span>
          </div>

          <div className="space-y-6 mb-10">
            <p className="font-body text-sm text-white/70 leading-relaxed">
              {t.convIntro}
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {t.convSteps.map((step, i) => (
                <div key={i} className="border border-white/10 bg-white/5 p-5">
                  <span className="font-heading text-2xl text-[#c9a84c]/40 block mb-3">{step.num}</span>
                  <p className="font-body text-sm text-white/60 leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-6 space-y-4">
              <p className="font-body text-sm text-white/70 leading-relaxed">
                {t.convMutual}
              </p>
              <p className="font-body text-sm text-white font-semibold">
                {t.convQuestions}
              </p>
            </div>
          </div>

          <div className="text-center">
            <a href="/" className="inline-flex items-center gap-2 font-body text-sm text-[#c9a84c] hover:text-[#d4b85c] transition-colors" data-testid="ty-back-home">
              {t.backHome} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
